import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, Subject } from 'rxjs';
import { SupportService } from 'src/app/services/core/support/support.service';
import { formatChatDay, formatMessageTime, isSameChatDay } from 'src/app/utils/chat-time';
import { sweetAlert } from 'src/app/utils/swal';
import { SUPPORT_PAGE_SIZES, SUPPORT_ROLE_LABELS, trackById } from 'src/app/utils/support.utils';
@UntilDestroy()

@Component({
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.css']
})
export class ChatThreadComponent implements OnInit, OnChanges {
  @Input() chatId: any;
  @Input() currentUser: any;
  @Input() isClosed = false;
  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLElement>;

  messages: any = [];
  hasOlderMessages = false;
  text = '';
  attachments: any = [];
  isUploading = false;
  isSending = false;
  roleLabels = SUPPORT_ROLE_LABELS;
  trackById = trackById;
  private readonly markAsReadSubject = new Subject<void>();

  constructor(private supportService: SupportService) { }

  ngOnInit() {
    this.listenChatMessages();
    this.listenMarkAsRead();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chatId'] && this.chatId) {
      this.text = '';
      this.attachments = [];
      this.getMessages();
    } else if (changes['currentUser']) {
      this.prepareMessages();
    }
  }

  getMessages() {
    this.supportService.getMessages(this.chatId, { limit: SUPPORT_PAGE_SIZES.messages }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      // the api returns the newest messages first
      this.messages = data.results.reverse();
      this.hasOlderMessages = data.totalPages > 1;
      this.prepareMessages();
      this.scrollToBottom();
      this.markAsReadSubject.next();
    }, (err) => {
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  loadOlderMessages() {
    const container = this.messagesContainer.nativeElement;
    const previousScrollHeight = container.scrollHeight;
    const queryParams = { before: this.messages[0].createdAt, limit: SUPPORT_PAGE_SIZES.messages };

    this.supportService.getMessages(this.chatId, queryParams).pipe(untilDestroyed(this)).subscribe((data: any) => {
      this.messages = [...data.results.reverse(), ...this.messages];
      this.hasOlderMessages = data.totalPages > 1;
      this.prepareMessages();
      // keep the message the user was looking at in place
      setTimeout(() => {
        container.scrollTop = container.scrollHeight - previousScrollHeight;
      });
    }, (err) => {
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  listenChatMessages() {
    this.supportService.chatMessage$.pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data.chatId !== this.chatId) {
        return;
      }
      this.addMessage(data.message);
      if (data.message.sender?.id !== this.currentUser?.id) {
        this.markAsReadSubject.next();
      }
    });
  }

  listenMarkAsRead() {
    this.markAsReadSubject.pipe(debounceTime(1000), untilDestroyed(this)).subscribe(() => this.markAsRead());
  }

  // the sender receives their own message from both the api and the socket
  addMessage(message: any) {
    const isAdded = this.messages.some((addedMessage: any) => addedMessage.id === message.id);
    if (!isAdded) {
      this.messages.push(message);
      this.prepareMessages();
      this.scrollToBottom();
    }
  }

  sendMessage() {
    const text = this.text.trim();
    // one at a time, clicking send again while it is on its way sent the same message again
    if (this.isSending || (!text && !this.attachments.length)) {
      return;
    }

    const payload: any = {};
    if (text) {
      payload.text = text;
    }
    if (this.attachments.length) {
      payload.attachments = this.attachments;
    }

    this.isSending = true;
    this.supportService.sendMessage(this.chatId, payload).pipe(untilDestroyed(this)).subscribe((message: any) => {
      this.isSending = false;
      this.text = '';
      this.attachments = [];
      this.addMessage(message);
    }, (err) => {
      this.isSending = false;
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  onSelectFiles(event: any) {
    const files: File[] = Array.from(event.target.files);
    event.target.value = '';
    if (!files.length) {
      return;
    }

    this.isUploading = true;
    this.supportService.uploadAttachments(files).pipe(untilDestroyed(this)).subscribe((uploadedFiles: any) => {
      this.attachments.push(...uploadedFiles);
      this.isUploading = false;
    }, (err) => {
      this.isUploading = false;
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  removeAttachment(index: number) {
    this.attachments.splice(index, 1);
  }

  markAsRead() {
    this.supportService.markChatAsRead(this.chatId).pipe(untilDestroyed(this)).subscribe();
  }

  // worked out once instead of on every change detection
  prepareMessages() {
    this.messages.forEach((message: any, index: number) => {
      const previousMessage = this.messages[index - 1];
      message.isMine = message.sender?.id === this.currentUser?.id;
      message.time = formatMessageTime(message.createdAt);
      message.attachments?.forEach((attachment: any) => {
        attachment.isImage = Boolean(attachment.mimeType?.startsWith('image/'));
      });
      const isNewDay = !previousMessage || !isSameChatDay(previousMessage.createdAt, message.createdAt);
      message.day = isNewDay ? formatChatDay(message.createdAt) : null;
    });
  }

  scrollToBottom() {
    // wait for the new messages to render
    setTimeout(() => {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    });
  }
}
